import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { FiPlus, FiEye, FiRefreshCw, FiUsers } from "react-icons/fi";
import toast from "react-hot-toast";
import API from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";

const ROTATING_CHANNELS = ["Linkedin"];
const ROTATION_HOURS = 72;
const MAX_RUNS_PER_JOB = 3;

const SYSTEM_CHANNEL_NAMES = new Set(["Jobego", "Google for jobs", "Linkedin"]);

const translations = {
  en: {
    title: "Distribution",
    channel: "Channel",
    status: "Status",
    views: "Views",
    active: "Active",
    inactive: "Inactive",
    queued: "Queued",
    no_posts: "No channels yet",
    no_posts_desc: "Toggle a channel above or add a custom one",
    add_channel: "Add Channel",
    candidates: "Candidates",
    displayed: "Displayed",
    live_since: "Live since {{date}}",
    rotates_around: "rotates ~{{date}}",
    rotating_badge_title: "This channel auto-rotates",
    reason_max_applicants: "Reached 20 applicants — rotated out of LinkedIn",
    reason_rotated_out_72h: "Rotated out after 72h to give another job the LinkedIn slot",
    reason_slot_full: "Waiting for a LinkedIn slot — all slots currently filled",
    reason_job_unpublished: "Job is no longer published",
    reason_unmanaged_org: "Organization is not in LinkedIn slot rotation",
    toggle_jobego_label: "Jobego",
    toggle_jobego_desc: "Live on jobego.co",
    toggle_google_label: "Google for Jobs",
    toggle_google_desc: "Indexed in the Google sitemap",
    toggle_linkedin_label: "LinkedIn rotation",
    toggle_linkedin_desc: "Enter the shared LinkedIn slot pool (max 3 runs)",
    cap_reached: "Maximum runs reached (3)",
    saving: "Saving…",
  },
  fr: {
    title: "Distribution",
    channel: "Canal",
    status: "Statut",
    views: "Vues",
    active: "Actif",
    inactive: "Inactif",
    queued: "En file",
    no_posts: "Aucun canal",
    no_posts_desc: "Activez un canal ci-dessus ou ajoutez-en un personnalisé",
    add_channel: "Ajouter un canal",
    candidates: "Candidats",
    displayed: "Affiché",
    live_since: "En ligne depuis le {{date}}",
    rotates_around: "tourne ~{{date}}",
    rotating_badge_title: "Ce canal tourne automatiquement",
    reason_max_applicants: "20 candidats atteints — sorti de la rotation LinkedIn",
    reason_rotated_out_72h: "Sorti après 72h pour céder le slot LinkedIn à un autre poste",
    reason_slot_full: "En attente d'un slot LinkedIn — tous les slots sont occupés",
    reason_job_unpublished: "L'offre n'est plus publiée",
    reason_unmanaged_org: "L'organisation n'est pas dans la rotation LinkedIn",
    toggle_jobego_label: "Jobego",
    toggle_jobego_desc: "En ligne sur jobego.co",
    toggle_google_label: "Google for Jobs",
    toggle_google_desc: "Indexé dans le sitemap Google",
    toggle_linkedin_label: "Rotation LinkedIn",
    toggle_linkedin_desc: "Entrer dans le pool partagé de slots LinkedIn (max 3 runs)",
    cap_reached: "Maximum de runs atteint (3)",
    saving: "Enregistrement…",
  },
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

const isRotatingChannel = (channelName) => ROTATING_CHANNELS.includes(channelName);

const renderDisplayedCell = (post, t) => {
  const rotating = isRotatingChannel(post.channel_name);
  const startedAt = formatDate(post.created_at);

  if (post.status === "active") {
    if (rotating && post.created_at) {
      const rotatesAt = new Date(new Date(post.created_at).getTime() + ROTATION_HOURS * 60 * 60 * 1000);
      return (
        <>
          {t("live_since", { date: startedAt })} · {t("rotates_around", { date: rotatesAt.toLocaleDateString() })}
        </>
      );
    }
    return t("live_since", { date: startedAt });
  }

  if (post.status === "queued") {
    return t("reason_slot_full");
  }

  const endedAt = formatDate(post.updated_at);
  const reason = rotating && post.rotation_reason ? ` · ${t(`reason_${post.rotation_reason}`)}` : "";
  return (
    <>
      {startedAt} → {endedAt}
      {reason}
    </>
  );
};

const ChannelToggle = ({ label, description, checked, onChange, disabled, disabledReason, busy }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <div className="min-w-0">
      <div className="text-sm font-medium text-gray-900">{label}</div>
      <div className="text-xs text-gray-500">{disabled && disabledReason ? disabledReason : description}</div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
        checked ? "bg-primary" : "bg-gray-200"
      } ${disabled || busy ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  </div>
);

export default function DistributionHub({ job, setJob, posts, setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [candidateCounts, setCandidateCounts] = useState({});
  const [savingFlag, setSavingFlag] = useState(null);

  const systemPosts = useMemo(
    () =>
      [...(posts || [])]
        .filter((p) => SYSTEM_CHANNEL_NAMES.has(p.channel_name))
        .sort((a, b) => {
          const order = { active: 0, queued: 1, inactive: 2 };
          return (order[a.status] ?? 3) - (order[b.status] ?? 3);
        }),
    [posts],
  );

  const linkedinRunCount = useMemo(
    () => (posts || []).filter((p) => p.channel_name === "Linkedin").length,
    [posts],
  );
  const linkedinCapReached = linkedinRunCount >= MAX_RUNS_PER_JOB && !job?.channel_linkedin;

  useEffect(() => {
    if (!systemPosts.length) {
      setCandidateCounts({});
      return;
    }
    let cancelled = false;
    const fetchCounts = async () => {
      const results = await Promise.all(
        systemPosts.map(async (post) => {
          const res = await API.post("/application/search", { job_post_id: post._id, limit: 1 });
          return [post._id, res.ok ? res.total || 0 : 0];
        }),
      );
      if (!cancelled) setCandidateCounts(Object.fromEntries(results));
    };
    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [systemPosts]);

  const refetchPosts = async () => {
    if (!setPosts) return;
    const res = await API.post("/post/search", { job_id: jobId, limit: 200 });
    if (res.ok) setPosts(res.data || []);
  };

  const toggleFlag = async (flag, nextValue) => {
    if (savingFlag) return;
    setSavingFlag(flag);
    const previous = job?.[flag];
    if (setJob) setJob({ ...job, [flag]: nextValue });
    try {
      const res = await API.put(`/job/${jobId}`, { [flag]: nextValue });
      if (!res.ok) {
        if (setJob) setJob({ ...job, [flag]: previous });
        toast.error(res.code || "Failed to update channel");
        return;
      }
      if (setJob) setJob(res.data);
      await refetchPosts();
    } catch (err) {
      if (setJob) setJob({ ...job, [flag]: previous });
      toast.error(err?.code || "Failed to update channel");
    } finally {
      setSavingFlag(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
        <button
          onClick={() => navigate("new")}
          className="btn btn-primary btn-small flex items-center gap-2"
        >
          <FiPlus className="w-4 h-4" />
          {t("add_channel")}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 divide-y divide-gray-100">
        <ChannelToggle
          label={t("toggle_jobego_label")}
          description={t("toggle_jobego_desc")}
          checked={!!job?.channel_jobego}
          onChange={(v) => toggleFlag("channel_jobego", v)}
          busy={savingFlag === "channel_jobego"}
        />
        <ChannelToggle
          label={t("toggle_google_label")}
          description={t("toggle_google_desc")}
          checked={!!job?.channel_google_for_jobs}
          onChange={(v) => toggleFlag("channel_google_for_jobs", v)}
          busy={savingFlag === "channel_google_for_jobs"}
        />
        <ChannelToggle
          label={t("toggle_linkedin_label")}
          description={t("toggle_linkedin_desc")}
          checked={!!job?.channel_linkedin}
          onChange={(v) => toggleFlag("channel_linkedin", v)}
          disabled={linkedinCapReached}
          disabledReason={t("cap_reached")}
          busy={savingFlag === "channel_linkedin"}
        />
      </div>

      {systemPosts.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <p className="text-sm font-medium text-gray-900 mb-1">{t("no_posts")}</p>
          <p className="text-sm text-gray-500">{t("no_posts_desc")}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-5 py-3">{t("channel")}</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-5 py-3">{t("status")}</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-5 py-3">{t("views")}</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-5 py-3">{t("candidates")}</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-5 py-3">{t("displayed")}</th>
              </tr>
            </thead>
            <tbody>
              {systemPosts.map((post) => (
                <tr
                  key={post._id}
                  onClick={() => navigate(`post/${post._id}`)}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {post.channel_logo ? (
                        <img src={post.channel_logo} alt={post.channel_name} className="w-8 h-8 object-contain rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-sm font-bold text-gray-400">{post.channel_name?.[0]}</span>
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-900">{post.channel_name || "—"}</span>
                      {isRotatingChannel(post.channel_name) && (
                        <FiRefreshCw
                          className="w-3.5 h-3.5 text-gray-400"
                          title={t("rotating_badge_title")}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {post.status === "active" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {t("active")}
                      </span>
                    ) : post.status === "queued" ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        {t("queued")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        {t("inactive")}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <FiEye className="w-3.5 h-3.5 text-gray-400" />
                      {post.viewed_count || 0}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <FiUsers className="w-3.5 h-3.5 text-gray-400" />
                      {candidateCounts[post._id] ?? 0}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">
                    {renderDisplayedCell(post, t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
